// web/geneProgression.ts — LONG-TERM gene progression: Fusion + Ascension.
//
// Solves "duplicate genes become meaningless": every drop now feeds one of two
// sinks, so it always has value.
//
//   • FUSION — combine 3 genes into ONE stronger gene (rarity +1, the summed trait
//     stacks of all three, plus a fusion bonus). A pure GENE sink for duplicates.
//   • ASCENSION — evolve a high-tier gene up a NAMED ladder (Venom Sac → Neurotoxin
//     Organ → Neural Corruption Gland → Hive Parasite Network → …). Each step
//     promotes rarity (until Legendary) and stacks its dominant trait, so power
//     keeps climbing — INFINITE progression via unbounded stacks + procedural names.
//
// Both operate on the REAL engine GeneInventory through its public primitives
// (insert / addTrait / retire / promoteToRarity), so fused/ascended genes flow into
// the genome-apply, box, military-strength and leaderboard systems unchanged. This
// class is pure logic (no DOM); ascension level is keyed by a gene's stable SERIAL
// so it survives id recycling and rebuilds deterministically on save-replay.

import { NIL, RARITY, GENE_TYPE_COUNT, GENE_TRAIT_COUNT } from '../src/types.ts';
import type { GeneInventory, GeneId } from '../src/sim/genes.ts';
import { GENE_TYPE_NAME, RARITY_NAME } from '../src/sim/geneData.ts';
import { genePowerOf } from '../src/sim/genePower.ts';
import type { GeneAdvice } from '../src/sim/advisor.ts';
import { validCastesForType, casteCompatLabel } from '../src/sim/casteGenes.ts';

/**
 * Named ascension ladder per GENE_TYPE (index = GENE_TYPE.*: Wing, Mandible,
 * VenomGland, Carapace, Antenna, Limb). Level 0 = the base organ. Beyond the last
 * named tier, names gain a "+N" suffix, so progression never runs out.
 */
const ASCENSION_LADDERS: readonly (readonly string[])[] = [
  ['Wing', 'Stormrider Wings', 'Tempest Membrane', 'Skyshatter Aspect', 'Worldwind Sovereign'],
  ['Mandible', 'Serrated Crushers', 'Bonesaw Maw', 'Carnage Pincers', 'Worldrender Jaws'],
  ['Venom Sac', 'Neurotoxin Organ', 'Neural Corruption Gland', 'Hive Parasite Network', 'Apex Plague Engine'],
  ['Carapace', 'Plated Bulwark', 'Adamant Shell', 'Living Fortress', 'Eternal Aegis'],
  ['Antenna', 'Sensory Array', 'Psionic Antennae', 'Mindweb Lattice', 'Omniscient Crown'],
  ['Limb', 'Sprinter Legs', 'Razor Limbs', 'Quicksilver Stride', 'Phase Strider'],
];

/** Mutation Energy cost to ascend = BASE × (currentLevel + 1) — escalating. */
const ASCEND_BASE_COST = 130;
/** Trait stacks added per ascension (grows with level — power keeps climbing). */
function ascendStacks(level: number): number {
  return 3 + Math.floor(level / 2);
}
/** Bonus stack added to a fused gene's dominant trait, on top of the summed stacks. */
const FUSE_BONUS = 1;
/** Genes required per fusion. */
export const FUSE_INPUTS = 3;

export interface GeneRowView {
  id: number;
  serial: number;
  type: number;
  rarity: number;
  traitTotal: number;
  label: string;          // engine describeGene(), or the ascended organ name
  power: number;          // Power Score (display-only, ascension-aware)
  ascension: number;      // 0 = un-ascended
  ascendName: string;     // current organ name at this ascension level
  canAscend: boolean;
  ascendCost: number;
  /**
   * Evolution Advisor verdict for THIS gene against the player's colony — how well it
   * FITS (need/identity), distinct from `power` (raw strength). Populated by
   * GameSession.geneRows() (which has the colony's diagnostics + identity); the pure
   * GeneProgression layer leaves it undefined. Display-only — never sim state.
   */
  advice?: GeneAdvice;
  /**
   * Caste-compatibility (src/sim/casteGenes.ts): the caste indices (ascending CASTE.*)
   * this gene's body-part type suits, plus a human label ("Soldier" /
   * "Worker + Flyer (Hybrid)"). Populated by GameSession.geneRows(); the pure
   * GeneProgression layer leaves these defaulted. Pure classification — never sim state.
   */
  castes: readonly number[];
  casteLabel: string;
  /**
   * Forecast % of its NATURAL caste's power this gene would add if equipped now — the
   * same band-bounded `geneApplyForecast` the box reveal shows, so the inventory previews
   * a gene's IMPACT, not just its raw score. Populated by GameSession.geneRows(); the pure
   * GeneProgression layer leaves it undefined. Display-only — never sim state.
   */
  gainPct?: number;
}

export interface TreeRow {
  type: number;
  baseName: string;
  ladder: string[];       // the named tiers (length 5)
  owned: number;          // genes of this type the player holds
  bestLevel: number;      // highest ascension reached for this type (-1 if none owned)
}

export interface FuseResult { ok: boolean; message: string; newSerial?: number; rarity?: number; }
export interface AscendResult { ok: boolean; message: string; name?: string; rarity?: number; cost?: number; }

export class GeneProgression {
  private readonly inv: GeneInventory;
  private readonly colony: number;
  /** serial → ascension level (rebuilt deterministically by replaying actions). */
  private readonly level = new Map<number, number>();

  constructor(inv: GeneInventory, colony = 0) {
    this.inv = inv;
    this.colony = colony;
  }

  // --- naming ----------------------------------------------------------------

  ascensionName(type: number, level: number): string {
    const ladder = (ASCENSION_LADDERS[type] ?? [GENE_TYPE_NAME[type] as string]) as readonly string[];
    const last = ladder.length - 1;
    if (level <= last) return ladder[level] as string;
    return `${ladder[last] as string} +${level - last}`;
  }

  ascensionOf(serial: number): number {
    return this.level.get(serial) ?? 0;
  }

  /** Highest ascension level across the player's held genes (for history/snapshots). */
  maxAscension(): number {
    let m = 0;
    for (let id = this.inv.firstOf(this.colony); id !== NIL; id = this.inv.nextOf(id)) {
      const lvl = this.ascensionOf(this.inv.serialOf(id) as number);
      if (lvl > m) m = lvl;
    }
    return m;
  }

  ladderFor(type: number): string[] {
    return [...((ASCENSION_LADDERS[type] ?? [GENE_TYPE_NAME[type] as string]) as readonly string[])];
  }

  // --- queries ---------------------------------------------------------------

  /** A gene is ascendable once it's Rare+, or if it has already begun ascending. */
  private canAscendId(id: GeneId): boolean {
    return (this.inv.rarityOf(id) as number) >= RARITY.Rare || this.ascensionOf(this.inv.serialOf(id) as number) > 0;
  }

  ascendCost(serial: number): number {
    return ASCEND_BASE_COST * (this.ascensionOf(serial) + 1);
  }

  private findBySerial(serial: number): GeneId {
    for (let id = this.inv.firstOf(this.colony); id !== NIL; id = this.inv.nextOf(id)) {
      if ((this.inv.serialOf(id) as number) === serial) return id;
    }
    return NIL;
  }

  private dominantTrait(id: GeneId): number {
    let best = 0;
    let bestC = -1;
    for (let t = 0; t < GENE_TRAIT_COUNT; t++) {
      const c = this.inv.traitOf(id, t) as number;
      if (c > bestC) { bestC = c; best = t; }
    }
    return best;
  }

  /** The player's gene list with ascension info, in deterministic inventory order. */
  rows(): GeneRowView[] {
    const out: GeneRowView[] = [];
    for (let id = this.inv.firstOf(this.colony); id !== NIL; id = this.inv.nextOf(id)) {
      const serial = this.inv.serialOf(id) as number;
      const type = this.inv.typeOf(id) as number;
      const asc = this.ascensionOf(serial);
      out.push({
        id,
        serial,
        type,
        rarity: this.inv.rarityOf(id) as number,
        traitTotal: this.inv.traitTotalOf(id) as number,
        label: this.inv.describeGene(id),
        power: genePowerOf(this.inv, id, asc),
        ascension: asc,
        ascendName: this.ascensionName(type, asc),
        canAscend: this.canAscendId(id),
        ascendCost: this.ascendCost(serial),
        castes: validCastesForType(type),
        casteLabel: casteCompatLabel(type),
      });
    }
    return out;
  }

  /** The progression tree: one ladder per gene type with the player's best reach. */
  tree(): TreeRow[] {
    const owned = new Int32Array(GENE_TYPE_COUNT);
    const best = new Int32Array(GENE_TYPE_COUNT).fill(-1);
    for (let id = this.inv.firstOf(this.colony); id !== NIL; id = this.inv.nextOf(id)) {
      const type = this.inv.typeOf(id) as number;
      owned[type] = (owned[type] as number) + 1;
      const lvl = this.ascensionOf(this.inv.serialOf(id) as number);
      if (lvl > (best[type] as number)) best[type] = lvl;
    }
    const rows: TreeRow[] = [];
    for (let type = 0; type < GENE_TYPE_COUNT; type++) {
      rows.push({
        type,
        baseName: GENE_TYPE_NAME[type] as string,
        ladder: this.ladderFor(type),
        owned: owned[type] as number,
        bestLevel: best[type] as number,
      });
    }
    return rows;
  }

  // --- operations ------------------------------------------------------------

  /**
   * Ascend the gene with `serial` one tier: promote rarity (until Legendary) and
   * stack its dominant trait, then advance its named level. Mutates in place (same
   * id/serial). Deterministic; the CALLER handles the energy cost.
   */
  ascend(serial: number): AscendResult {
    const id = this.findBySerial(serial);
    if (id === NIL) return { ok: false, message: 'That gene is no longer available.' };
    if (!this.canAscendId(id)) {
      return { ok: false, message: 'Only Rare+ genes (or already-ascended ones) can ascend.' };
    }
    const cost = this.ascendCost(serial);
    const level = this.ascensionOf(serial);

    const rarity = this.inv.rarityOf(id) as number;
    if (rarity < RARITY.Legendary) this.inv.promoteToRarity(id, rarity + 1);
    this.inv.addTrait(id, this.dominantTrait(id), ascendStacks(level));
    this.level.set(serial, level + 1);

    const type = this.inv.typeOf(id) as number;
    const name = this.ascensionName(type, level + 1);
    return { ok: true, message: `Ascended → ${name}`, name, rarity: this.inv.rarityOf(id) as number, cost };
  }

  /**
   * Fuse 3 genes into one stronger gene: child rarity = best input +1 (capped
   * Legendary), carrying the SUMMED trait stacks of all three plus a fusion bonus,
   * and inheriting the highest ascension level among them. The three inputs are
   * consumed. Deterministic; allocation is a handful of locals.
   */
  fuse(serials: readonly number[], tick: number): FuseResult {
    if (serials.length !== FUSE_INPUTS) {
      return { ok: false, message: `Select exactly ${FUSE_INPUTS} genes to fuse.` };
    }
    const ids: GeneId[] = [];
    const seen = new Set<number>();
    for (const s of serials) {
      if (seen.has(s)) return { ok: false, message: 'Pick three different genes.' };
      seen.add(s);
      const id = this.findBySerial(s);
      if (id === NIL) return { ok: false, message: 'One of those genes is gone.' };
      ids.push(id);
    }

    // Read everything BEFORE retiring.
    const combined = new Int32Array(GENE_TRAIT_COUNT);
    let bestRarity = -1;
    let baseId = ids[0] as GeneId;
    let bestKey = -1;
    let childLevel = 0;
    for (const id of ids) {
      const r = this.inv.rarityOf(id) as number;
      const tt = this.inv.traitTotalOf(id) as number;
      const key = r * 1000 + tt;
      if (key > bestKey) { bestKey = key; baseId = id; }
      if (r > bestRarity) bestRarity = r;
      for (let t = 0; t < GENE_TRAIT_COUNT; t++) combined[t] = (combined[t] as number) + (this.inv.traitOf(id, t) as number);
      const lvl = this.ascensionOf(this.inv.serialOf(id) as number);
      if (lvl > childLevel) childLevel = lvl;
    }
    const childType = this.inv.typeOf(baseId) as number;
    const childRarity = Math.min(bestRarity + 1, RARITY.Legendary);
    const originColony = this.inv.originColonyOf(baseId) as number;
    const sourceCaste = this.inv.sourceCasteOf(baseId) as number;

    // Retire the three inputs FIRST (frees slots so the insert always fits).
    for (const id of ids) {
      this.level.delete(this.inv.serialOf(id) as number);
      this.inv.retire(id);
    }

    const child = this.inv.insert(this.colony, childType, childRarity, originColony, sourceCaste, tick);
    if (child === NIL) return { ok: false, message: 'Fusion failed (no inventory slot).' };

    let dom = 0;
    let domC = -1;
    for (let t = 0; t < GENE_TRAIT_COUNT; t++) {
      const c = combined[t] as number;
      if (c > 0) this.inv.addTrait(child, t, c);
      if (c > domC) { domC = c; dom = t; }
    }
    this.inv.addTrait(child, dom, FUSE_BONUS); // fusion bonus → strictly stronger than the sum
    if (childLevel > 0) this.level.set(this.inv.serialOf(child) as number, childLevel);

    const rname = RARITY_NAME[childRarity] as string;
    return {
      ok: true,
      newSerial: this.inv.serialOf(child) as number,
      rarity: childRarity,
      message: `Fused 3 → one ${rname} ${this.ascensionName(childType, childLevel)}.`,
    };
  }
}
